import { supabase } from './supabase.js'
import { todayISO } from './format.js'

/**
 * The calendar is a read. Nothing here writes, and nothing here is stored —
 * every row already lives somewhere else with its own dates.
 *
 * What it shows is deliberately thin: names and dates. No amounts. A figure
 * belongs to the thing itself, one tap away, so a month full of money still
 * reads as a month.
 */

export function monthBounds(monthKey) {
  const [y, m] = monthKey.split('-').map(Number)
  const first = new Date(y, m - 1, 1)
  const last = new Date(y, m, 0)
  return { from: iso(first), to: iso(last), first, last }
}

function iso(d) {
  const off = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - off).toISOString().slice(0, 10)
}

/**
 * Six rows of seven, always. A grid that changes height between months makes
 * the whole screen jump when he pages through it.
 */
export function monthGrid(monthKey) {
  const { first } = monthBounds(monthKey)
  const start = new Date(first)
  start.setDate(1 - first.getDay())

  const days = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    days.push({
      key: iso(d),
      date: d,
      day: d.getDate(),
      inMonth: d.getMonth() === first.getMonth(),
      isToday: iso(d) === todayISO(),
    })
  }
  return days
}

/**
 * Everything dated that overlaps the month.
 *
 * Money is not here on purpose — no installment charges, no renewals, no
 * payouts. He was explicit: the calendar carries the name, the thing carries
 * the number.
 */
export async function getMonthEvents(monthKey) {
  const { from, to } = monthBounds(monthKey)

  const [projects, reminders, tasks, google] = await Promise.all([
    /* A project overlaps the month if it starts before the month ends and
       ends after the month starts. A project with no end is a single day. */
    supabase
      .from('projects')
      .select('id, title, starts_on, ends_on, category_id, closed_at')
      .not('starts_on', 'is', null)
      .lte('starts_on', to)
      .or('ends_on.is.null,ends_on.gte.' + from),
    supabase
      .from('reminders')
      .select('id, body, remind_at, done_at')
      .gte('remind_at', from + 'T00:00:00')
      .lte('remind_at', to + 'T23:59:59'),
    supabase
      .from('tasks')
      .select('id, body, due_on, status, category_id')
      .not('due_on', 'is', null)
      .gte('due_on', from)
      .lte('due_on', to),
    /* Google's events, mirrored here by the Worker. Read-only: this app is
       not the place he edits his Google calendar. */
    supabase
      .from('gcal_events')
      .select('uid, title, starts_at, ends_at, all_day')
      .gte('starts_at', from + 'T00:00:00')
      .lte('starts_at', to + 'T23:59:59')
      .order('starts_at'),
  ])

  return {
    projects: (projects.data ?? []).filter((p) => !p.ends_on || p.ends_on >= from),
    reminders: reminders.data ?? [],
    tasks: tasks.data ?? [],
    google: google.data ?? [],
  }
}

/** Everything on one day, in the order he would read it. */
export function eventsOn(dayKey, events) {
  const out = []

  for (const p of events.projects) {
    const start = p.starts_on
    const end = p.ends_on || p.starts_on
    if (dayKey < start || dayKey > end) continue
    out.push({
      kind: 'project',
      id: p.id,
      title: p.title,
      categoryId: p.category_id,
      /* Day 3 of 6 is more use than the date he is already looking at. */
      note: end > start
        ? 'Day ' + (dayNumber(start, dayKey)) + ' of ' + (dayNumber(start, end))
        : null,
      project: p,
    })
  }

  for (const t of events.tasks) {
    if (t.due_on !== dayKey) continue
    out.push({
      kind: 'task', id: t.id, title: t.body,
      categoryId: t.category_id,
      note: t.status === 'Done' ? 'Done' : null,
      done: t.status === 'Done',
    })
  }

  for (const r of events.reminders) {
    if (r.remind_at.slice(0, 10) !== dayKey) continue
    out.push({
      kind: 'reminder', id: r.id, title: r.body,
      at: r.remind_at, done: !!r.done_at,
    })
  }

  for (const g of events.google ?? []) {
    if (localDay(g.starts_at) !== dayKey) continue
    out.push({
      kind: 'google', id: g.uid, title: g.title,
      at: g.all_day ? null : g.starts_at,
      note: 'Google Calendar',
    })
  }

  return out
}

/* An event at 6:10am UTC is not on yesterday's square for someone in Manila. */
function localDay(iso) {
  const d = new Date(iso)
  const off = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - off).toISOString().slice(0, 10)
}

function dayNumber(from, to) {
  const a = new Date(from + 'T00:00:00')
  const b = new Date(to + 'T00:00:00')
  return Math.round((b - a) / 86400000) + 1
}

/**
 * Where each project's band sits on a given day: does it start here, end
 * here, both, or run straight through. Drawn as one bar rather than the same
 * word repeated across six cells.
 */
export function bandsOn(dayKey, projects) {
  return projects
    .filter((p) => {
      const end = p.ends_on || p.starts_on
      return dayKey >= p.starts_on && dayKey <= end
    })
    .map((p) => {
      const end = p.ends_on || p.starts_on
      const startsHere = dayKey === p.starts_on
      const endsHere = dayKey === end
      return {
        id: p.id,
        title: p.title,
        project: p,
        /* Only the first cell carries the words; the rest are the same bar
           continuing, which is what makes it read as one thing. */
        showTitle: startsHere,
        edge: startsHere && endsHere ? 'solo' : startsHere ? 'start' : endsHere ? 'end' : 'mid',
      }
    })
}

/** Dots for the single-day things, so a busy day still reads at a glance. */
export function dotsOn(dayKey, events) {
  const out = []
  if (events.reminders.some((r) => r.remind_at.slice(0, 10) === dayKey)) out.push('reminder')
  if (events.tasks.some((t) => t.due_on === dayKey)) out.push('task')
  if ((events.google ?? []).some((g) => localDay(g.starts_at) === dayKey)) out.push('google')
  return out
}
